import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Spinner, Badge } from "react-bootstrap";
import ModalConfirmar from "../../components/ModalConfirmar";
import * as paisesApi from "../../api/paises";
import { PERFILES } from "../../api/catalogosEstaticos";
import * as usuariosApi from "../../api/usuarios";

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try { setUsuarios(await usuariosApi.listarUsuarios()); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const confirmarEliminar = async () => {
    await usuariosApi.eliminarUsuario(aEliminar.usuarioId);
    setAEliminar(null);
    cargar();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Mantenimiento &gt; Usuarios</h4>
        <Button size="sm" variant="primary" onClick={() => { setEditando(null); setShowModal(true); }}>
          <i className="bi bi-plus-lg me-1"></i>Nuevo usuario
        </Button>
      </div>

      {cargando ? <Spinner animation="border" /> : (
        <div className="fs-card p-0 overflow-hidden">
          <Table responsive hover className="table-fs table-fs-responsive mb-0 align-middle">
            <thead>
              <tr><th>Nombres</th><th>Email</th><th>Perfil</th><th>País</th><th>Email validado</th><th>Estado</th><th></th></tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.usuarioId}>
                  <td data-label="Nombres">{u.nombres}</td>
                  <td data-label="Email">{u.email}</td>
                  <td data-label="Perfil"><Badge bg={u.perfil === "Administrador" ? "dark" : "info"}>{u.perfil}</Badge></td>
                  <td data-label="País">{u.pais}</td>
                  <td data-label="Email validado">
                    <Badge bg={u.emailValidado ? "success" : "warning"} text={u.emailValidado ? undefined : "dark"}>
                      {u.emailValidado ? "Sí" : "No"}
                    </Badge>
                  </td>
                  <td data-label="Estado"><Badge bg={u.activo ? "success" : "secondary"}>{u.activo ? "Activo" : "Inactivo"}</Badge></td>
                  <td data-label="Acciones" className="text-end">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => { setEditando(u); setShowModal(true); }}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => setAEliminar(u)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <ModalUsuario show={showModal} onHide={() => setShowModal(false)} usuario={editando} onGuardado={() => { setShowModal(false); cargar(); }} />
      <ModalConfirmar
        show={!!aEliminar}
        titulo="Desactivar usuario"
        mensaje={`¿Desactivar al usuario "${aEliminar?.nombres}"? (borrado lógico)`}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setAEliminar(null)}
      />
    </div>
  );
}

function ModalUsuario({ show, onHide, usuario, onGuardado }) {
  const [form, setForm] = useState({});
  const [paises, setPaises] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (show) {
      paisesApi.listarPaises().then(setPaises);
      setForm(usuario
        ? { nombres: usuario.nombres, email: usuario.email, paisId: usuario.paisId, edad: usuario.edad,
            perfilId: PERFILES.find((p) => p.nombre === usuario.perfil)?.perfilId || "", activo: usuario.activo, password: "",
            accesoPagosExtra: usuario.accesoPagosExtra }
        : { nombres: "", email: "", paisId: "", edad: "", perfilId: "", activo: true, password: "", accesoPagosExtra: false });
    }
  }, [show, usuario]);

  const actualizar = (campo) => (e) => {
    const valor = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [campo]: valor });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await usuariosApi.upsertUsuario({
        usuarioId: usuario?.usuarioId ?? null,
        nombres: form.nombres,
        email: form.email,
        paisId: Number(form.paisId),
        edad: Number(form.edad),
        perfilId: Number(form.perfilId),
        activo: !!form.activo,
        password: form.password || null,
        accesoPagosExtra: !!form.accesoPagosExtra,
      });
      onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title style={{ fontSize: "1.1rem" }}>{usuario ? "Editar usuario" : "Nuevo usuario"}</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nombres</Form.Label>
            <Form.Control value={form.nombres || ""} onChange={actualizar("nombres")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" value={form.email || ""} onChange={actualizar("email")} required />
          </Form.Group>
          <div className="row">
            <div className="col-7">
              <Form.Group className="mb-3">
                <Form.Label>País</Form.Label>
                <Form.Select value={form.paisId || ""} onChange={actualizar("paisId")} required>
                  <option value="">Selecciona...</option>
                  {paises.map((p) => <option key={p.paisId} value={p.paisId}>{p.nombre}</option>)}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-5">
              <Form.Group className="mb-3">
                <Form.Label>Edad</Form.Label>
                <Form.Control type="number" value={form.edad || ""} onChange={actualizar("edad")} required />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Perfil</Form.Label>
            <Form.Select value={form.perfilId || ""} onChange={actualizar("perfilId")} required>
              <option value="">Selecciona...</option>
              {PERFILES.map((p) => <option key={p.perfilId} value={p.perfilId}>{p.nombre}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Contraseña {usuario && <span className="text-muted small">(dejar vacío para no cambiarla)</span>}</Form.Label>
            <Form.Control type="password" value={form.password || ""} onChange={actualizar("password")} required={!usuario} minLength={6} />
          </Form.Group>
          <Form.Check type="switch" label="Usuario activo" checked={!!form.activo} onChange={actualizar("activo")} className="mb-2" />
          <Form.Check type="switch" label="Acceso a Pagos Extra" checked={!!form.accesoPagosExtra} onChange={actualizar("accesoPagosExtra")} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={guardando}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
